// components/Sidebar.jsx
import { useState } from "react"
import { Link } from "react-router-dom"
import {
  LogOut, Sparkles, ChevronDown,
} from "lucide-react"
import resolutLogo from "../assets/resolut-logo.png"

const FF = "'Inter', sans-serif"
const SPRITE = "/jobright-icons.svg"

// Reference the Jobright sidebar sprite icons directly
function NavSpriteIcon({ id, size = 20 }) {
  return (
    <svg width={size} height={size} aria-hidden="true" style={{ flexShrink: 0, display: "block" }}>
      <use href={`${SPRITE}#${id}`} />
    </svg>
  )
}

export default function Sidebar({
  activeTab,
  onTabChange,
  C,
  user,
  userData,
  onSignOut,
  applicationsBadge = 0,
  pulseApplications = 0,
}) {
  const [showProfile, setShowProfile] = useState(false)

  const displayName   = userData?.name || user?.displayName || ""
  const avatarInitial = displayName ? displayName[0].toUpperCase() : "?"
  const activeNavId   = activeTab || "console"

  // Each nav item names the Jobright sprite icon to use (with selected variant)
  const NAV = [
    { id: "console",      label: "Home",         icon: "nav-jobs",     iconActive: "nav-jobs-selected" },
    { id: "applications", label: "Applications", icon: "nav-autofill", iconActive: "nav-autofill" },
    { id: "wild",         label: "In the wild",  icon: "nav-agent",    iconActive: "nav-agent-selected" },
    { id: "inbox",        label: "Inbox",        icon: "nav-alert",    iconActive: "nav-alert" },
    { id: "profile",      label: "Profile",      icon: "nav-profile",  iconActive: "nav-profile-selected" },
  ]

  const handleNav = (item) => {
    if (item.disabled) return
    onTabChange?.(item.id)
  }

  return (
    <>
      <style>{`
        .sb2-item {
          display: flex; align-items: center; gap: 12px;
          width: 100%; padding: 8px 12px;
          border: 1px solid transparent; background: none;
          cursor: pointer; font-family: ${FF};
          font-size: 13px; font-weight: 450;
          color: #000000;
          border-radius: 12px; text-decoration: none;
          transition: background 0.15s, color 0.15s, border-color 0.15s;
          text-align: left;
          letter-spacing: 0;
          line-height: 20px;
        }
        .sb2-item:hover  { background: #f7f8f9; color: #000000; }
        .sb2-item.active {
          background: #f7f8f9;
          color: #000000;
          font-weight: 600;
        }
        .sb2-item.active:hover { background: #f0f1f2; }
        @keyframes sb2-menu { from { opacity:0; transform:translateY(-6px); } to { opacity:1; transform:translateY(0); } }
        @keyframes sb2-badge-pop {
          0%   { transform: translateX(-4px); opacity: 0; }
          100% { transform: translateX(0);    opacity: 1; }
        }
        @keyframes sb2-row-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(18,18,18,0.0); }
          40%      { box-shadow: 0 0 0 4px rgba(18,18,18,0.14); }
        }
        @keyframes sb2-spinner-rot {
          to { transform: rotate(360deg); }
        }
        .sb2-badge {
          display: inline-flex; align-items: center; gap: 5px;
          font-family: ${FF};
          font-size: 10.5px; font-weight: 500;
          letter-spacing: 0.01em;
          color: #71717a;
          animation: sb2-badge-pop 0.3s ease-out;
        }
        .sb2-badge b { color: #18181b; font-weight: 700; }
        .sb2-spinner {
          width: 8px; height: 8px;
          border-radius: 50%;
          border: 1.4px solid rgba(18,18,18,0.18);
          border-top-color: #121212;
          animation: sb2-spinner-rot 0.75s linear infinite;
        }
        .sb2-item.pulse { animation: sb2-row-pulse 0.9s ease-out; }
      `}</style>

      <aside style={{
        width: 240,
        minWidth: 240,
        flexShrink: 0,
        height: "100vh",
        position: "sticky",
        top: 0,
        overflowY: "auto",
        background: "#ffffff",
        display: "flex",
        flexDirection: "column",
        zIndex: 10,
      }}>

        {/* Logo */}
        <div style={{ padding: "18px 16px 14px" }}>
          <Link to="/" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }}>
            <img
              src={resolutLogo}
              alt="Resolut"
              style={{ width: 34, height: 34, borderRadius: 7, objectFit: "contain", flexShrink: 0 }}
            />
            <span style={{
              fontSize: 19, fontWeight: 700, color: "#18181b",
              fontFamily: FF, letterSpacing: "-0.025em",
            }}>
              Resolut
            </span>
          </Link>
        </div>

        {/* Nav */}
        <nav style={{ padding: "2px 10px", flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
          <p style={{
            fontSize: 11, fontWeight: 500, letterSpacing: "0.08em",
            color: "rgba(0,0,0,0.45)", fontFamily: FF, textTransform: "uppercase",
            padding: "10px 12px 6px", margin: 0,
          }}>
            Workspace
          </p>

          {NAV.map(item => {
            const isActive = activeNavId === item.id
            const isApps = item.id === "applications"
            const showAppsBadge = isApps && applicationsBadge > 0
            const pulseClass = isApps && pulseApplications > 0 ? " pulse" : ""
            return (
              <button
                key={item.id}
                onClick={() => handleNav(item)}
                className={`sb2-item${isActive ? " active" : ""}${pulseClass}`}
                data-nav-id={item.id}
              >
                <NavSpriteIcon id={isActive ? item.iconActive : item.icon} size={20} />
                <span style={{ flex: 1 }}>{item.label}</span>
                {showAppsBadge && (
                  <span key={applicationsBadge} className="sb2-badge" data-apps-badge="1">
                    <span className="sb2-spinner" />
                    <span><b>{applicationsBadge > 99 ? "99+" : applicationsBadge}</b> running</span>
                  </span>
                )}
                {!showAppsBadge && item.badge && (
                  <span style={{
                    fontSize: 9, fontWeight: 700, letterSpacing: "0.08em",
                    background: "#f4e3b2",
                    color: "#7a5a12",
                    borderRadius: 4, padding: "2px 6px",
                  }}>
                    {item.badge}
                  </span>
                )}
              </button>
            )
          })}
        </nav>

        {/* Bottom section */}
        <div style={{ padding: "10px 10px 14px", display: "flex", flexDirection: "column", gap: 2 }}>

          <div style={{ height: 1, background: "#eeeeee", margin: "4px 4px 8px" }} />

          {/* Profile button */}
          <div style={{ position: "relative" }}>
            <button
              onClick={() => setShowProfile(p => !p)}
              style={{
                display: "flex", alignItems: "center", gap: 10,
                width: "100%", padding: "6px 10px",
                border: "1px solid transparent",
                background: showProfile ? "#f4f4f5" : "transparent",
                borderRadius: 7, cursor: "pointer", textAlign: "left",
                transition: "background 0.15s",
              }}
              onMouseEnter={e => e.currentTarget.style.background = "#f4f4f5"}
              onMouseLeave={e => { if (!showProfile) e.currentTarget.style.background = "transparent" }}
            >
              <div style={{
                width: 26, height: 26, borderRadius: 6,
                background: "#ececea",
                border: "1px solid #e0e0dd",
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#1a1a1a", fontFamily: FF }}>
                  {avatarInitial}
                </span>
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{
                  fontSize: 12, fontWeight: 600, color: "#18181b",
                  margin: 0, fontFamily: FF,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {displayName || "—"}
                </p>
                <p style={{
                  fontSize: 10, color: "#71717a",
                  margin: 0, fontFamily: FF,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {user?.email || ""}
                </p>
              </div>

              <ChevronDown
                size={13}
                color="#71717a"
                style={{ flexShrink: 0, transform: showProfile ? "rotate(180deg)" : "none", transition: "transform 0.15s" }}
              />
            </button>

            {showProfile && (
              <>
                <div onClick={() => setShowProfile(false)} style={{ position: "fixed", inset: 0, zIndex: 149 }} />
                <div style={{
                  position: "absolute",
                  bottom: "calc(100% + 6px)", left: 0, right: 0,
                  background: "#fff",
                  border: "1px solid #e4e4e7",
                  borderRadius: 6, zIndex: 150, overflow: "hidden",
                  boxShadow: "0 4px 6px -1px rgba(0,0,0,0.06), 0 16px 40px rgba(0,0,0,0.1)",
                  animation: "sb2-menu 0.15s cubic-bezier(0.22,1,0.36,1)",
                }}>
                  <div style={{ padding: "10px 12px 8px", borderBottom: "1px solid #f4f4f5" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                      <span style={{ fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", color: "#a1a1aa", fontFamily: FF }}>Plan</span>
                      <span style={{ fontSize: 10, fontWeight: 700, color: "#18181b", fontFamily: FF, letterSpacing: "0.04em" }}>PRO</span>
                    </div>
                    <button
                      onClick={() => alert("No need, Enjoy :)")}
                      style={{
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
                        width: "100%", padding: "7px 0",
                        background: "#18181b",
                        border: "none",
                        borderRadius: 4, cursor: "pointer",
                        fontSize: 11, fontWeight: 700,
                        color: "#fff", fontFamily: FF,
                        transition: "background 0.15s",
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = "#000"}
                      onMouseLeave={e => e.currentTarget.style.background = "#18181b"}
                    >
                      <Sparkles size={11} /> Upgrade
                    </button>
                  </div>

                  <button
                    onClick={onSignOut}
                    style={{
                      width: "100%", display: "flex", alignItems: "center", gap: 8,
                      padding: "10px 12px",
                      background: "none", border: "none", cursor: "pointer",
                      fontSize: 13, fontWeight: 500,
                      color: "#dc2626", fontFamily: FF,
                      transition: "background 0.1s",
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = "#fef2f2"}
                    onMouseLeave={e => e.currentTarget.style.background = "none"}
                  >
                    <LogOut size={14} color="#dc2626" />
                    Sign Out
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </aside>
    </>
  )
}
